import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { salesApi } from '../../services/salesApi.js';

const EMPTY = { customerId: '', contractId: '', subject: '', description: '', priority: 'MEDIUM' };

export function TicketCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const customers = useQuery({ queryKey: ['sales', 'customers'], queryFn: () => salesApi.listCustomers({ limit: 200 }).then((r) => r.items), enabled: open });
  const contracts = useQuery({ queryKey: ['sales', 'contracts'], queryFn: () => salesApi.listContracts().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(EMPTY); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => salesApi.createTicket(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['sales', 'tickets'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.customerId || !form.subject.trim()) { setError(t('tk.error.required')); return; }
    save.mutate({
      customerId: form.customerId,
      contractId: form.contractId || null,
      subject: form.subject.trim(),
      description: form.description || null,
      priority: form.priority,
    });
  }

  const customerContracts = (contracts.data || []).filter((c) => c.customerId === form.customerId && c.status === 'ACTIVE');

  return (
    <Modal open={open} onClose={onClose} title={t('tk.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('tk.field.customer')}</span>
            <select className="input" value={form.customerId} onChange={set('customerId')} required>
              <option value="">-</option>
              {(customers.data || []).map((c) => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            </select>
          </label>
          <label className="field">
            <span className="field-label">{t('tk.field.priority')}</span>
            <select className="input" value={form.priority} onChange={set('priority')}>
              {['LOW', 'MEDIUM', 'HIGH', 'URGENT'].map((p) => <option key={p} value={p}>{t(`tk.priority.${p}`)}</option>)}
            </select>
          </label>
        </div>
        {customerContracts.length > 0 && (
          <label className="field">
            <span className="field-label">{t('tk.field.contract')}</span>
            <select className="input" value={form.contractId} onChange={set('contractId')}>
              <option value="">{t('tk.field.noContract')}</option>
              {customerContracts.map((c) => <option key={c.id} value={c.id}>{c.contractNumber} · {c.title}</option>)}
            </select>
          </label>
        )}
        <label className="field">
          <span className="field-label">{t('tk.field.subject')}</span>
          <input className="input" value={form.subject} onChange={set('subject')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('tk.field.description')}</span>
          <textarea className="input" rows={4} value={form.description} onChange={set('description')} />
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
